import { axiosClient } from '@nx-frontend/shared/api';
import { EditorInstance, NoteDto } from '@nx-frontend/shared/types';
import { Template } from '@nx-frontend/shared/types';

interface MiniBlot {
  map: {
    get: (key: string) => string;
  };
  prev: MiniBlot | null;
  remove: () => void;
}

interface NodeWithBlot {
  __blot: {
    blot: MiniBlot;
  };
}

async function copyTaskList(sourceTaskListId: string, targetNoteGlobalId: string, targetWorkspaceId: string) {
  const [workspaceId, taskListId] = sourceTaskListId.split('_');
  const res = await axiosClient.post<{ globalId: string }>(
    `/v2/api/templates/taskLists/${workspaceId}/${taskListId}/copy`,
    {
      targetNoteGlobalId,
      targetWorkspaceId,
    }
  );
  return res.data;
}

export async function pasteTemplateIntoPage(editor: EditorInstance, template: Template, note: NoteDto) {
  let pageStr = JSON.stringify(template.page);

  // looking for task lists, they match regexp {{{_tasklist_:[a-zA-Z0-9_]+}}}
  const re = /\{\{\{_tasklist_:([a-zA-Z0-9_]+)\}\}\}/g;
  const taskListIds = [];
  let match;
  while ((match = re.exec(pageStr)) !== null) {
    taskListIds.push(match[1]);
    // remove workspace part from taskListId
    pageStr = pageStr.replace(match[0], '');
  }

  // looking for pseudo attachments to create, they have regexp {{{_attach_:(.+?)}}}
  const reAttachments = /\{\{\{_attach_:(.+?)\}\}\}/g;
  let matchAttachments;
  while ((matchAttachments = reAttachments.exec(pageStr)) !== null) {
    const [url, ...settings] = matchAttachments[1].split('|');
    const settingsObj: Record<string, unknown> = {};
    for (const setting of settings) {
      const [key, value] = setting.split('=');
      settingsObj[key] = value;
    }
    // creating file blot
    const map = editor.store.createMapForBlot(
      editor.parchment.query('file'),
      Object.assign(
        {
          attachmentGlobalId: 'psimg:' + url,
        },
        settingsObj
      )
    );
    const blotId = map.get('id');

    if (blotId && typeof blotId === 'string') {
      pageStr = pageStr.replace(matchAttachments[0], blotId);
    }
  }

  const page = JSON.parse(pageStr);

  editor.scroll.selection.setRange({ index: 0, length: 0 });
  editor.plugins.pasteManager.pasteNimbusOps(page);

  if (taskListIds.length) {
    // TODO does not actually work with multiple tasks lists
    // need to set ref mark in the task list map and then replace task lists by this mark
    for (const taskListId of taskListIds) {
      // copy task lists
      const { globalId } = await copyTaskList(taskListId, note.globalId, note.workspaceId);
      // find task list blot to replace
      const nodes = Array.from(editor.container.querySelectorAll('.tasks-list'));
      for (const node of nodes) {
        const __blot = (node as unknown as NodeWithBlot).__blot;
        const blot = __blot.blot;
        const after = blot.prev ? blot.prev.map.get('id') : null;
        blot.remove();
        editor.scroll.addBlot('tasks-list', {
          tasksListId: globalId,
          after,
        });
      }
    }
  }
}
